// Screenshots of the organizer back office, used on the marketing surface.
//
// Why this file exists. Every marketing page was text plus icons, and the only
// way a prospect could see the dashboard was to put a card into the Paddle
// checkout. That is backwards for a competition that buys on a committee cycle,
// and it means the person who actually evaluates the back office (the
// competition manager) never got to evaluate it. These shots are the artifact
// that answers "does this look like a real product" without a call.
//
// This module is the SINGLE source of truth for those images. Marketing pages
// and the product tour read it and never hardcode a path, so:
//
//   1. A page can never point at a file that is not committed. `getProductShot`
//      returns undefined for a key with no image yet and callers render nothing,
//      and tests/unit/config/productShots.test.ts fails the build if a declared
//      entry has no file on disk.
//   2. Re-capturing after a UI change is one command and no page edits:
//      `node scripts/capture-product-shots.mjs` writes exactly these keys.
//
// The images themselves are captured from the seeded demo leagues (see
// src/config/demo.ts and scripts/README-demo-seed.md), never from a real
// customer's competition.
//
// Keep `width`/`height` equal to the real pixel dimensions of the file. They are
// rendered as explicit img attributes so the browser reserves the box before the
// image loads, which keeps cumulative layout shift near zero on the prerendered
// marketing pages.

/** Stable identifier for one screenshot. Also its filename stem. */
export type ProductShotKey =
  | "schedule"
  | "score-entry"
  | "teams"
  | "venues"
  | "officials"
  | "discipline"
  | "stats"
  | "standings"
  | "sponsor-report"
  | "audit"
  | "integration";

export interface ProductShot {
  key: ProductShotKey;
  /** Public path of the committed image. */
  src: string;
  /** Real pixel dimensions of the file. */
  width: number;
  height: number;
  /** Short, descriptive alt text. Screen readers and non-loading images. */
  alt: string;
  /** One line shown under the image. What the buyer should take from it. */
  caption: string;
}

/** Capture viewport. The script and these dimensions must agree. */
export const PRODUCT_SHOT_WIDTH = 1440;
export const PRODUCT_SHOT_HEIGHT = 900;

/** Directory the capture script writes into, relative to public/. */
export const PRODUCT_SHOT_DIR = "product-shots";

// Declared shots. An entry appears here only once its image is committed AND a
// human has looked at it. `ProductShotFigure` renders null for an undeclared
// key, so a screen that captured thin (an empty tab, a loading skeleton, a
// panel that did not open) is simply left out and the page keeps its narrative.
// Do not declare a key just because a file exists for it.
//
// Officials, Venues and Discipline used to be excluded here because
// supabase/seed_demo_leagues.sql seeded no `officials`, `league_venues` or
// `discipline_*` rows and they would have captured as empty states. That is no
// longer true: scripts/lib/demo-operations.mjs seeds all three across every
// demo competition. They are the strongest screens for a semi-professional
// competition, so they are in the capture list.
export const PRODUCT_SHOTS: ProductShot[] = [];

/** One shot by key, or undefined when it has not been captured yet. */
export function getProductShot(key: ProductShotKey): ProductShot | undefined {
  return PRODUCT_SHOTS.find((shot) => shot.key === key);
}

/** True when at least one shot is available to render. */
export function hasProductShots(): boolean {
  return PRODUCT_SHOTS.length > 0;
}
